import React, { memo, useMemo } from "react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Lock, Paperclip } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { cn } from "@/lib/utils";
import { Transaction } from "@/context/FinanceContext";
import { ColumnConfig } from "./ColumnVisibilitySelector";
import { formatTransactionCurrency } from "@/lib/formatters";

// Helper to parse date-only strings as local dates (avoids timezone shift)
const parseLocalDate = (dateString: string): Date => {
  if (!dateString) return new Date(NaN);
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [y, m, d] = dateString.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const d2 = new Date(dateString);
  return new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
};

// Helper function to get transaction status (3 estados: Conciliado > Pago > Pendente)
const getTransactionStatus = (transaction: Transaction): string => {
  if (transaction.reconciled === true) return "Conciliado";
  if (transaction.settled === true) return "Pago";
  
  if (transaction.dueDate) {
    const today = new Date();
    const dueDate = parseLocalDate(transaction.dueDate);
    const yesterday = new Date(today);
    
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (dueDate <= yesterday) {
      return "Vencido";
    }
  }
  
  return "Em Aberto";
};

interface Category {
  id: string;
  name: string;
  color?: string;
}

interface Account {
  id: string;
  name: string;
  color?: string;
}

interface PaymentMethod {
  id: string;
  name: string;
}

interface CostCenter {
  id: string;
  name: string;
}

interface Supplier {
  id: string;
  name: string;
  cnpj?: string;
  anexo?: string;
}

interface MemoizedCellProps {
  transaction: Transaction;
  column: ColumnConfig;
  categoryById: Map<string, Category>;
  accountById: Map<string, Account>;
  paymentMethodById: Map<string, PaymentMethod>;
  costCenterById: Map<string, CostCenter>;
  supplierById: Map<string, Supplier>;
  attachmentCount?: number;
  onOpenAttachments?: (transaction: Transaction) => void;
}


// Memoized date formatter
const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return "-";
  try {
    return format(parseISO(dateString), "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "-";
  }
};

export const MemoizedCell = memo<MemoizedCellProps>(({
  transaction,
  column,
  categoryById,
  accountById,
  paymentMethodById,
  costCenterById,
  supplierById,
  attachmentCount = 0,
  onOpenAttachments,
}) => {

  const value = useMemo(() => {
    // Validar se a transação tem dados válidos
    if (!transaction || !transaction.id) return "Erro";
    
    try {
      switch (column.key) {
        case "issueDate":
          return formatDate(transaction.issueDate);
        case "description":
          return transaction.description ?? '';
        case "value":
          return formatTransactionCurrency(transaction.value, transaction.type);
        case "category":
          return categoryById.get(transaction.categoryId)?.name || "-";
        case "account":
          return accountById.get(transaction.accountId)?.name || "-";
        case "supplier":
          return supplierById.get(transaction.supplierId || "")?.name || "-";
        case "type":
          // Se for transferência, exibir como "Transferência"
          if (transaction.kind === "transferencia") {
            return "Transferência";
          }
          return transaction.type === "receita" ? "Receita" : "Despesa";
        case "status":
          return getTransactionStatus(transaction);
        case "dueDate":
          return formatDate(transaction.dueDate);
        case "paymentDate":
          return transaction.settled === true ? formatDate(transaction.paymentDate) : "-";
        case "paymentMethod":
          return paymentMethodById.get(transaction.paymentMethodId || "")?.name || "-";
        case "costCenter":
          return costCenterById.get(transaction.costCenterId || "")?.name || "-";
        case "parcels":
          return transaction.parcels ? `${transaction.parcelIndex || 1}/${transaction.parcels}` : "-";
        default:
          return "";
      }
    } catch (error) {
      console.warn('Erro ao processar célula:', error, { transaction, column });
      return "Erro";
    }
  }, [transaction, column.key, categoryById, accountById, paymentMethodById, costCenterById, supplierById]);

  if (column.key === "attachments") {
    return (
      <button
        type="button"
        onClick={() => onOpenAttachments?.(transaction)}
        title={attachmentCount > 0 ? `${attachmentCount} comprovante(s)` : "Anexar comprovante"}
        aria-label="Comprovantes"
        className={cn(
          "relative flex items-center justify-center h-7 w-7 rounded-md transition-colors",
          attachmentCount > 0
            ? "text-primary hover:bg-primary/10"
            : "text-muted-foreground hover:bg-muted"
        )}
      >
        <Paperclip className="h-4 w-4" />
        {attachmentCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-[3px] rounded-full bg-primary text-primary-foreground text-[9px] leading-[14px] font-semibold">
            {attachmentCount}
          </span>
        )}
      </button>
    );
  }

  if (column.key === "status") {

    const statusColors = {
      "Conciliado": "bg-emerald-600 text-white",
      "Pago": "bg-blue-500 text-white",
      "Em Aberto": "bg-warning text-warning-foreground", 
      "Vencido": "bg-destructive text-destructive-foreground"
    };
    
    return (
      <div className="flex items-center justify-center gap-1">
        <Badge 
          variant="secondary" 
          className={cn(
            "text-xs px-2 py-1 whitespace-nowrap",
            statusColors[value as keyof typeof statusColors] || "bg-muted text-muted-foreground"
          )}
        >
          {value}
        </Badge>
        {transaction.reconciled === true && (
          <Lock
            className="h-3 w-3 text-emerald-600"
            aria-label="Conciliado - bloqueado para edição"
          />
        )}
      </div>
    );
  }

  if (column.key === "type") {
    const getTypeColor = () => {
      if (value === "Transferência") return "bg-blue-500 text-white";
      if (value === "Receita") return "bg-success text-success-foreground";
      return "bg-destructive text-destructive-foreground";
    };
    
    return (
      <Badge 
        variant="secondary" 
        className={cn(
          "text-xs px-2 py-1 whitespace-nowrap",
          getTypeColor()
        )}
      >
        {value}
      </Badge>
    );
  }

  if (column.key === "value") {
    const getValueColor = () => {
      if (transaction.kind === "transferencia") return "text-blue-600";
      return transaction.type === "receita" ? "text-success" : "text-destructive";
    };
    
    return (
      <div className={cn(
        "font-semibold text-sm text-center whitespace-nowrap",
        getValueColor()
      )}>
        {value}
      </div>
    );
  }

  if (column.key === "supplier") {
    const supplier = supplierById.get(transaction.supplierId || "");
    if (supplier && (supplier.cnpj || supplier.anexo)) {
      return (
        <HoverCard openDelay={150} closeDelay={80}>
          <HoverCardTrigger asChild>
            <div className="text-sm truncate block text-center whitespace-nowrap cursor-default">
              {value}
            </div>
          </HoverCardTrigger>
          <HoverCardContent className="w-64 z-50" side="top">
            <div className="space-y-1.5 text-left">
              <p className="text-sm font-semibold">{supplier.name}</p>
              <div className="text-xs text-muted-foreground space-y-1">
                <p><span className="font-medium text-foreground">CNPJ:</span> {supplier.cnpj || "-"}</p>
                <p className="break-words"><span className="font-medium text-foreground">Anexo:</span> {supplier.anexo || "-"}</p>
              </div>
            </div>
          </HoverCardContent>
        </HoverCard>
      );
    }
  }

  return (
    <div className="text-sm truncate block text-center whitespace-nowrap" title={value}>
      {value}
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for optimal performance - include ALL transaction fields that could change
  return (
    prevProps.transaction.id === nextProps.transaction.id &&
    prevProps.transaction.description === nextProps.transaction.description &&
    prevProps.transaction.value === nextProps.transaction.value &&
    prevProps.transaction.type === nextProps.transaction.type &&
    prevProps.transaction.categoryId === nextProps.transaction.categoryId &&
    prevProps.transaction.accountId === nextProps.transaction.accountId &&
    prevProps.transaction.costCenterId === nextProps.transaction.costCenterId &&
    prevProps.transaction.paymentMethodId === nextProps.transaction.paymentMethodId &&
    prevProps.transaction.supplierId === nextProps.transaction.supplierId &&
    prevProps.transaction.issueDate === nextProps.transaction.issueDate &&
    prevProps.transaction.dueDate === nextProps.transaction.dueDate &&
    prevProps.transaction.paymentDate === nextProps.transaction.paymentDate &&
    prevProps.transaction.parcelMode === nextProps.transaction.parcelMode &&
    prevProps.transaction.parcels === nextProps.transaction.parcels &&
    prevProps.transaction.parcelIndex === nextProps.transaction.parcelIndex &&
    prevProps.transaction.issueDateMode === nextProps.transaction.issueDateMode &&
    prevProps.transaction.settled === nextProps.transaction.settled &&
    prevProps.transaction.reconciled === nextProps.transaction.reconciled &&
    prevProps.column.key === nextProps.column.key &&
    prevProps.attachmentCount === nextProps.attachmentCount &&

    // Compare only the specific lookup values used by this cell to avoid reference comparison issues
    prevProps.categoryById.get(prevProps.transaction.categoryId)?.name === nextProps.categoryById.get(nextProps.transaction.categoryId)?.name &&
    prevProps.accountById.get(prevProps.transaction.accountId)?.name === nextProps.accountById.get(nextProps.transaction.accountId)?.name &&
    prevProps.paymentMethodById.get(prevProps.transaction.paymentMethodId || "")?.name === nextProps.paymentMethodById.get(nextProps.transaction.paymentMethodId || "")?.name &&
    prevProps.costCenterById.get(prevProps.transaction.costCenterId || "")?.name === nextProps.costCenterById.get(nextProps.transaction.costCenterId || "")?.name &&
    prevProps.supplierById.get(prevProps.transaction.supplierId || "")?.name === nextProps.supplierById.get(nextProps.transaction.supplierId || "")?.name &&
    prevProps.supplierById.get(prevProps.transaction.supplierId || "")?.cnpj === nextProps.supplierById.get(nextProps.transaction.supplierId || "")?.cnpj &&
    prevProps.supplierById.get(prevProps.transaction.supplierId || "")?.anexo === nextProps.supplierById.get(nextProps.transaction.supplierId || "")?.anexo
  );
});

MemoizedCell.displayName = "MemoizedCell";